/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: emailSettingsApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API client functions for Email Settings module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { EmailConfig } from '../types';

export interface EmailConfigListResponse {
  data: EmailConfig[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface TestEmailResponse {
  success: boolean;
  message: string;
  error?: string;
}

export interface SendEmailResponse {
  success: boolean;
  message: string;
  error?: string;
  logId?: number;
}

export interface EmailLogsResponse {
  data: unknown[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export async function fetchEmailConfigs(
  params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  },
  tenantSlug: string,
  userId?: number
): Promise<EmailConfigListResponse> {
  const query = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  });

  if (params.search?.trim()) {
    query.set('search', params.search.trim());
  }

  const response = await fetch(buildApiUrl(`/api/email-settings?${query.toString()}`), {
    headers: getApiHeaders(tenantSlug, userId),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const payload = await response.json();
  return {
    data: (payload.data as EmailConfig[]) ?? [],
    pagination: {
      page: payload.pagination?.page ?? params.page,
      limit: payload.pagination?.limit ?? params.limit,
      total: payload.pagination?.total ?? 0,
      pages: payload.pagination?.pages ?? 0,
    },
  };
}

export async function fetchEmailConfig(
  id: string,
  tenantSlug: string,
  userId?: number
): Promise<EmailConfig> {
  const response = await fetch(buildApiUrl(`/api/email-settings/${id}`), {
    headers: getApiHeaders(tenantSlug, userId),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const payload = await response.json();
  return (payload.data ?? payload) as EmailConfig;
}

export async function createEmailConfig(
  config: Partial<EmailConfig>,
  tenantSlug: string,
  userId?: number
): Promise<EmailConfig> {
  const response = await fetch(buildApiUrl('/api/email-settings'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(tenantSlug, userId),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(config),
  });

  if (!response.ok) {
    const errorPayload = await response.json().catch(() => null);
    throw new Error(errorPayload?.message ?? `HTTP ${response.status}`);
  }

  const result = await response.json().catch(() => ({}));
  return (result?.data ?? result) as EmailConfig;
}

export async function updateEmailConfig(
  id: string,
  config: Partial<EmailConfig>,
  tenantSlug: string,
  userId?: number
): Promise<EmailConfig> {
  const response = await fetch(buildApiUrl(`/api/email-settings/${id}`), {
    method: 'PUT',
    headers: {
      ...getApiHeaders(tenantSlug, userId),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(config),
  });

  if (!response.ok) {
    const errorPayload = await response.json().catch(() => null);
    throw new Error(errorPayload?.message ?? `HTTP ${response.status}`);
  }

  const result = await response.json().catch(() => ({}));
  return (result?.data ?? result) as EmailConfig;
}

export async function deleteEmailConfig(
  id: string | number,
  tenantSlug: string,
  userId?: number
): Promise<void> {
  const response = await fetch(buildApiUrl(`/api/email-settings/${id}`), {
    method: 'DELETE',
    headers: getApiHeaders(tenantSlug, userId),
  });

  if (!response.ok) {
    let message = 'Failed to delete Email Configuration';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors and use default message
    }
    throw new Error(message);
  }
}

export async function testEmailConfig(
  configId: string | number | undefined,
  testEmail: string,
  tenantSlug: string,
  userId?: number
): Promise<TestEmailResponse> {
  const response = await fetch(buildApiUrl('/api/email-settings/test'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(tenantSlug, userId),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ configId, testEmail }),
  });

  if (!response.ok) {
    throw new Error('Failed to test email configuration');
  }

  return (await response.json()) as TestEmailResponse;
}

export async function sendEmail(
  options: {
    to: string;
    toName?: string;
    subject: string;
    htmlContent?: string;
    textContent?: string;
    templateId?: string;
    templateVariables?: Record<string, unknown>;
    configId?: string | number;
  },
  tenantSlug: string,
  userId?: number
): Promise<SendEmailResponse> {
  const response = await fetch(buildApiUrl('/api/email-settings/send'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(tenantSlug, userId),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(options),
  });

  if (!response.ok) {
    throw new Error('Failed to send email');
  }

  return (await response.json()) as SendEmailResponse;
}

export async function fetchEmailLogs(
  params: {
    page: number;
    limit: number;
    status?: 'pending' | 'sent' | 'failed';
    startDate?: Date;
    endDate?: Date;
  },
  tenantSlug: string,
  userId?: number
): Promise<EmailLogsResponse> {
  const query = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit),
  });

  if (params.status) {
    query.set('status', params.status);
  }
  if (params.startDate) {
    query.set('startDate', params.startDate.toISOString());
  }
  if (params.endDate) {
    query.set('endDate', params.endDate.toISOString());
  }

  const response = await fetch(buildApiUrl(`/api/email-settings/logs?${query.toString()}`), {
    headers: getApiHeaders(tenantSlug, userId),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const payload = await response.json();
  return {
    data: payload.data ?? [],
    pagination: payload.pagination ?? {
      page: params.page,
      limit: params.limit,
      total: 0,
      pages: 0,
    },
  };
}

